/**
 * Hook de ações de contrato - Encapsula chamadas às Edge Functions
 * para gerenciar o fluxo de vida de um contrato digital.
 *
 * RELEASE: v1.1.0 - 2026-09-03 - Bloqueios do Workflow usam aviso modal global
 * RELEASE: v1.0.0 - 2026-02-16
 * Ações: Gerar PDF, Enviar para Assinatura, Consultar Status, Cancelar, Baixar PDF
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { showToast } from '@/lib/toast';
import { createLogger } from '@/lib/logger';
import { resolveContractPdfBucket } from '@/lib/contracts/pdfBucketResolver';
import { useWorkflowGuardNotice } from '@/providers/WorkflowGuardNoticeProvider';

// Logger dedicado para ações de contrato
const logger = createLogger('ContractActions');

// =============================================================================
// TIPOS
// =============================================================================

/** Parâmetros comuns para todas as ações */
interface ContractActionParams {
  contractId: string;
  tenantId: string;
}

/** Parâmetros para download de PDF */
interface DownloadPdfParams {
  pdfStoragePath: string;
  /** Nome do arquivo para o download. Padrão: 'contrato.pdf' */
  fileName?: string;
}

// =============================================================================
// HOOK PRINCIPAL
// =============================================================================

/**
 * Hook que expõe mutations para cada ação do fluxo de contrato.
 * Cada mutation invalida o cache relevante após sucesso.
 */
export function useContractActions(contractId: string, tenantId: string) {
  const queryClient = useQueryClient();
  const workflowNotice = useWorkflowGuardNotice();

  /** Invalida queries relacionadas ao contrato após qualquer ação */
  const invalidateContractQueries = () => {
    queryClient.invalidateQueries({ queryKey: ['contract-detail', contractId] });
    queryClient.invalidateQueries({ queryKey: ['contracts', 'deal'] });
    queryClient.invalidateQueries({ queryKey: ['contract-timeline', contractId] });
    // v1.1.0: Invalida dados CRM do Kanban de Conversas (status do contrato no card)
    queryClient.invalidateQueries({ queryKey: ['kanban-conversation-crm-data'] });
  };


  // ---------------------------------------------------------------------------
  // 1. Gerar PDF (draft -> queued_pdf -> processing_pdf -> generated)
  // Agora assíncrono via PGMQ - retorna 202 Accepted imediatamente
  // ---------------------------------------------------------------------------
  const generatePdf = useMutation({
    mutationFn: async () => {
      logger.info('Enfileirando geração de PDF', { contractId, tenantId });

      const { data, error } = await supabase.functions.invoke('contract-enqueue', {
        body: { action: 'generate_pdf', contract_id: contractId, tenant_id: tenantId },
      });

      if (error) {
        logger.error('Erro ao enfileirar geração de PDF', { error: error.message });
        throw new Error(error.message || 'Erro ao enfileirar geração de PDF');
      }

      if (data?.error) {
        logger.error('Enqueue retornou erro', { data });
        throw new Error(data.error);
      }

      logger.info('PDF enfileirado com sucesso', { contractId, queueMsgId: data?.queue_msg_id });
      return data;
    },
    onSuccess: () => {
      showToast.success('PDF enfileirado!', 'A geração está sendo processada em segundo plano.');
      invalidateContractQueries();
    },
    onError: (error: Error) => {
      if (workflowNotice.showWorkflowGuardNotice(error)) return;
      showToast.error('Erro ao enfileirar PDF', error.message);
    },
  });

  // ---------------------------------------------------------------------------
  // 2. Enviar para Assinatura (generated -> queued_signature -> processing_signature -> sent)
  // Agora assíncrono via PGMQ - retorna 202 Accepted imediatamente
  // ---------------------------------------------------------------------------
  const sendForSignature = useMutation({
    mutationFn: async () => {
      logger.info('Enfileirando envio para assinatura', { contractId, tenantId });

      const { data, error } = await supabase.functions.invoke('contract-enqueue', {
        body: { action: 'send_signature', contract_id: contractId, tenant_id: tenantId },
      });

      if (error) {
        logger.error('Erro ao enfileirar assinatura', { error: error.message });
        throw new Error(error.message || 'Erro ao enfileirar envio para assinatura');
      }

      if (data?.error) {
        logger.error('Enqueue retornou erro', { data });
        throw new Error(data.error);
      }

      logger.info('Assinatura enfileirada com sucesso', { contractId, queueMsgId: data?.queue_msg_id });
      return data;
    },
    onSuccess: () => {
      showToast.success('Assinatura enfileirada!', 'O envio está sendo processado em segundo plano.');
      invalidateContractQueries();
    },
    onError: (error: Error) => {
      if (workflowNotice.showWorkflowGuardNotice(error)) return;
      showToast.error('Erro ao enfileirar assinatura', error.message);
    },
  });

  // ---------------------------------------------------------------------------
  // 3. Consultar Status (sent -> verifica mudanças)
  // ---------------------------------------------------------------------------
  const checkStatus = useMutation({
    mutationFn: async () => {
      logger.info('Consultando status do contrato', { contractId, tenantId });

      const { data, error } = await supabase.functions.invoke('signature-proxy', {
        body: {
          operation: 'get_status',
          contract_id: contractId,
          tenant_id: tenantId,
        },
      });

      // Priorizar mensagem do body (mais descritiva) sobre o erro genérico do SDK
      if (data?.error) {
        logger.error('Proxy retornou erro ao consultar', { data });
        throw new Error(data.error);
      }

      if (error) {
        logger.error('Erro ao consultar status', { error: error.message });
        throw new Error(error.message || 'Erro ao consultar status');
      }

      logger.info('Status consultado com sucesso', { contractId, status: data?.status });
      return data;
    },
    onSuccess: (data) => {
      showToast.info('Status atualizado', data?.message || 'Dados sincronizados com o provider.');
      invalidateContractQueries();
    },
    onError: (error: Error) => {
      showToast.error('Erro ao consultar status', error.message);
    },
  });

  // ---------------------------------------------------------------------------
  // 4. Cancelar Contrato (sent -> cancelled)
  // ---------------------------------------------------------------------------
  const cancelContract = useMutation({
    mutationFn: async () => {
      logger.info('Cancelando contrato', { contractId, tenantId });

      const { data, error } = await supabase.functions.invoke('signature-proxy', {
        body: {
          operation: 'cancel',
          contract_id: contractId,
          tenant_id: tenantId,
        },
      });

      // Priorizar mensagem do body (mais descritiva) sobre o erro genérico do SDK
      if (data?.error) {
        logger.error('Proxy retornou erro ao cancelar', { data });
        throw new Error(data.error);
      }

      if (error) {
        logger.error('Erro ao cancelar contrato', { error: error.message });
        throw new Error(error.message || 'Erro ao cancelar contrato');
      }

      logger.info('Contrato cancelado com sucesso', { contractId });
      return data;
    },
    onSuccess: () => {
      showToast.success('Contrato cancelado', 'O contrato foi cancelado no provider.');
      invalidateContractQueries();
    },
    onError: (error: Error) => {
      showToast.error('Erro ao cancelar contrato', error.message);
    },
  });

  // ---------------------------------------------------------------------------
  // 5. Baixar PDF assinado (signed -> download)
  // ---------------------------------------------------------------------------
  const downloadPdf = useMutation({
    mutationFn: async ({ pdfStoragePath, fileName }: DownloadPdfParams) => {
      logger.info('Baixando PDF do storage', { pdfStoragePath, fileName });

      // Baixa o arquivo como blob para forçar download com extensão .pdf.
      // O bucket é resolvido pelo prefixo do path: PDFs enviados no wizard
      // ficam em `anexos`; PDFs gerados/assinados ficam em `contratos`.
      const bucket = resolveContractPdfBucket(pdfStoragePath);
      const { data: blobData, error } = await supabase.storage
        .from(bucket)
        .download(pdfStoragePath);

      if (error || !blobData) {
        logger.error('Erro ao baixar PDF', { error: error?.message });
        throw new Error(error?.message || 'Erro ao baixar PDF do storage');
      }

      // Cria link temporário para forçar download com nome personalizado
      const url = URL.createObjectURL(blobData);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName ?? 'contrato.pdf';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      logger.info('PDF baixado com sucesso', { pdfStoragePath, fileName });
      return { success: true };
    },
    onError: (error: Error) => {
      showToast.error('Erro ao baixar PDF', error.message);
    },
  });

  // ---------------------------------------------------------------------------
  // 6. Recuperar PDF Assinado (error|signed -> reenfileira save_signed_pdf)
  // Usado quando o erro ocorreu apenas na etapa de captura do PDF assinado,
  // ou seja, o contrato já foi assinado mas signed_pdf_storage_path é NULL.
  // ---------------------------------------------------------------------------
  const retrySaveSignedPdf = useMutation({
    mutationFn: async () => {
      logger.info('Reenfileirando recuperação do PDF assinado', { contractId, tenantId });

      const { data, error } = await supabase.functions.invoke('contract-enqueue', {
        body: { action: 'save_signed_pdf', contract_id: contractId, tenant_id: tenantId },
      });

      if (error) {
        logger.error('Erro ao reenfileirar save_signed_pdf', { error: error.message });
        throw new Error(error.message || 'Erro ao recuperar PDF assinado');
      }

      if (data?.error) {
        logger.error('Enqueue retornou erro ao reenfileirar save_signed_pdf', { data });
        throw new Error(data.error);
      }

      logger.info('save_signed_pdf enfileirado com sucesso', { contractId, queueMsgId: data?.queue_msg_id });
      return data;
    },
    onSuccess: () => {
      showToast.info('Recuperando PDF assinado...', 'O sistema irá buscar e salvar o PDF assinado em segundo plano.');
      invalidateContractQueries();
    },
    onError: (error: Error) => {
      showToast.error('Erro ao recuperar PDF assinado', error.message);
    },
  });

  // ---------------------------------------------------------------------------
  // 7. Reenviar Link de Assinatura (sent -> reenvia para pendentes)
  // ---------------------------------------------------------------------------
  const resendSignature = useMutation({
    mutationFn: async (signatureId?: string) => {
      logger.info('Reenviando link de assinatura', { contractId, tenantId, signatureId });

      const body: Record<string, unknown> = {
        operation: 'resend',
        contract_id: contractId,
        tenant_id: tenantId,
      };

      // Se signatureId informado, reenviar apenas para esse signatário
      if (signatureId) {
        body.signature_id = signatureId;
      }

      const { data, error } = await supabase.functions.invoke('signature-proxy', { body });

      // Priorizar mensagem do body (mais descritiva) sobre o erro genérico do SDK
      if (data?.error) {
        logger.error('Proxy retornou erro ao reenviar', { data });
        throw new Error(data.error);
      }

      if (error) {
        logger.error('Erro ao reenviar assinatura', { error: error.message });
        throw new Error(error.message || 'Erro ao reenviar link de assinatura');
      }

      logger.info('Link reenviado com sucesso', { contractId });
      return data;
    },
    onSuccess: (data) => {
      showToast.success('Link reenviado!', data?.message || 'Os signatários receberão o link novamente.');
      invalidateContractQueries();
    },
    onError: (error: Error) => {
      showToast.error('Erro ao reenviar link', error.message);
    },
  });

  // ---------------------------------------------------------------------------
  // 8. Adicionar signatário (generated/sent -> add_signer)
  // ---------------------------------------------------------------------------
  const addSigner = useMutation({
    mutationFn: async (signerData: {
      signer_name: string;
      signer_email?: string;
      signer_phone?: string;
      signer_role?: string;
      delivery_method?: string;
    }) => {
      logger.info('Adicionando signatário', { contractId, tenantId, signerName: signerData.signer_name });

      const { data, error } = await supabase.functions.invoke('signature-proxy', {
        body: {
          operation: 'add_signer',
          contract_id: contractId,
          tenant_id: tenantId,
          ...signerData,
        },
      });

      // Priorizar mensagem do body (mais descritiva) sobre o erro genérico do SDK
      if (data?.error) {
        logger.error('Proxy retornou erro ao adicionar signatário', { data });
        throw new Error(data.error);
      }

      if (error) {
        logger.error('Erro ao adicionar signatário', { error: error.message });
        throw new Error(error.message || 'Erro ao adicionar signatário');
      }

      logger.info('Signatário adicionado com sucesso', { contractId });
      return data;
    },
    onSuccess: (data) => {
      showToast.success('Signatário adicionado', data?.message || 'Signatário adicionado com sucesso.');
      invalidateContractQueries();
    },
    onError: (error: Error) => {
      showToast.error('Erro ao adicionar signatário', error.message);
    },
  });

  // ---------------------------------------------------------------------------
  // 9. Remover signatário (generated/sent -> remove_signer)
  // ---------------------------------------------------------------------------
  const removeSigner = useMutation({
    mutationFn: async (signatureId: string) => {
      logger.info('Removendo signatário', { contractId, tenantId, signatureId });

      const { data, error } = await supabase.functions.invoke('signature-proxy', {
        body: {
          operation: 'remove_signer',
          contract_id: contractId,
          tenant_id: tenantId,
          signature_id: signatureId,
        },
      });

      // Priorizar mensagem do body (mais descritiva) sobre o erro genérico do SDK
      if (data?.error) {
        logger.error('Proxy retornou erro ao remover signatário', { data });
        throw new Error(data.error);
      }

      if (error) {
        logger.error('Erro ao remover signatário', { error: error.message });
        throw new Error(error.message || 'Erro ao remover signatário');
      }

      logger.info('Signatário removido com sucesso', { contractId });
      return data;
    },
    onSuccess: (data) => {
      showToast.success('Signatário removido', data?.message || 'Signatário removido com sucesso.');
      invalidateContractQueries();
    },
    onError: (error: Error) => {
      showToast.error('Erro ao remover signatário', error.message);
    },
  });

  // ---------------------------------------------------------------------------
  // 9.1. Editar signatário (draft/generated/sent -> update_signer)
  // RELEASE: 2026-08-24 — corrige nome/e-mail/telefone/papel/forma de entrega de
  // signatário PENDENTE. Em contratos já enviados o proxy substitui o signatário
  // na autenticadora (novo link + novo convite).
  // ---------------------------------------------------------------------------
  const updateSigner = useMutation({
    mutationFn: async (input: {
      signature_id: string;
      signer_name: string;
      signer_email?: string;
      signer_phone?: string;
      signer_role?: string;
      delivery_method?: string;
    }) => {
      logger.info('Atualizando signatário', { contractId, tenantId, signatureId: input.signature_id });

      const { data, error } = await supabase.functions.invoke('signature-proxy', {
        body: {
          operation: 'update_signer',
          contract_id: contractId,
          tenant_id: tenantId,
          ...input,
        },
      });

      // Mensagem do body é mais descritiva que o erro genérico do SDK
      if (data?.error) {
        logger.error('Proxy retornou erro ao atualizar signatário', { data });
        throw new Error(data.error);
      }

      if (error) {
        logger.error('Erro ao atualizar signatário', { error: error.message });
        throw new Error(error.message || 'Erro ao atualizar signatário');
      }

      logger.info('Signatário atualizado com sucesso', { contractId });
      return data;
    },
    onSuccess: (data) => {
      showToast.success('Signatário atualizado', data?.message || 'Dados do signatário atualizados.');
      invalidateContractQueries();
    },
    onError: (error: Error) => {
      showToast.error('Erro ao atualizar signatário', error.message);
    },
  });


  // ---------------------------------------------------------------------------
  // 10. Excluir Rascunho (draft -> exclusão permanente)
  // Apenas contratos com status 'draft' podem ser excluídos.
  // Remove primeiro as assinaturas (FK) e depois o contrato.
  // ---------------------------------------------------------------------------
  // ---------------------------------------------------------------------------
  // 10. Excluir Rascunho via RPC (SECURITY DEFINER)
  // Usa função RPC delete_draft_contract que valida tenant, status draft,
  // e remove dependências (signatures, provider_tracking) atomicamente.
  // Vendedores sem policy DELETE conseguem excluir normalmente.
  // ---------------------------------------------------------------------------
  const deleteContract = useMutation({
    mutationFn: async () => {
      logger.info('Excluindo contrato rascunho via RPC', { contractId, tenantId });

      const { data, error } = await supabase.rpc('delete_draft_contract', {
        p_contract_id: contractId,
        p_tenant_hint: tenantId,
      });

      if (error) {
        logger.error('Erro ao excluir contrato via RPC', { error: error.message });
        throw new Error(error.message || 'Erro ao excluir contrato');
      }

      logger.info('Contrato rascunho excluído com sucesso via RPC', { contractId });
      return data;
    },
    onSuccess: () => {
      showToast.success('Rascunho excluído', 'O contrato rascunho foi removido com sucesso.');
      invalidateContractQueries();
    },
    onError: (error: Error) => {
      showToast.error('Erro ao excluir rascunho', error.message);
    },
  });

  return {
    generatePdf,
    sendForSignature,
    checkStatus,
    cancelContract,
    downloadPdf,
    retrySaveSignedPdf,
    resendSignature,
    addSigner,
    removeSigner,
    updateSigner,

    deleteContract,
  };
}
